import React, { useState, useEffect } from 'react';
import FolderIcon from '@mui/icons-material/Folder';
import {
    EditButton,
    Edit,
    Create,
    SimpleForm,
    ReferenceInput,
    SelectInput,
    BooleanInput,
    NumberInput,
    TextInput,
    useNotify,
    useRedirect,
    useGetOne,
    useRefresh,
    Confirm
} from 'react-admin';
import { Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import SortableTable from './components/SortableTable';
import { httpClient, publicBase } from './dataProvider';

const DeleteCourseWizardButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const url = `${publicBase}/course_wizard/${record.id}`;
            await httpClient(url, { method: 'DELETE' });
            notify('Course wizard entry deleted successfully', { type: 'success' });
            refresh();
            window.location.reload();
        } catch (error) {
            console.error('Error deleting course wizard entry:', error);
            notify('Failed to delete course wizard entry', { type: 'error' });
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleClick}
                color="error"
                startIcon={<DeleteIcon />}
            >
                Delete
            </Button>
            <Confirm
                isOpen={open}
                loading={loading}
                title="Delete Course Wizard Entry"
                content="Are you sure you want to delete this entry? This action cannot be undone."
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

// List view for course_wizard
export const CourseWizardList = () => {
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const url = `${publicBase}/course_wizard?end=1000`; // Fetch all for client-side sort
        httpClient(url)
            .then(({ json }) => {
                setMaterials(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching course wizard:', error);
                setLoading(false);
            });
    }, []);

    const columns = [
        { key: 'status', header: 'Status' },
        { key: 'version', header: 'Version' },
        { key: 'rag_kb_source', header: 'KB Source' },
        { key: 'prompt_template_name', header: 'Template' },
        {
            key: 'created_at',
            header: 'Created At',
            render: (record) => new Date(record.created_at).toLocaleString()
        },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <EditButton record={record} resource="course_wizard" />
                    <DeleteCourseWizardButton record={record} />
                </div>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <h2>Course Wizard</h2>
            <SortableTable data={materials} columns={columns} />
        </div>
    );
};

// Helper: render inputs from a JSON Schema (basic types)
const SchemaFields = ({ schema, value, onChange }) => {
    const properties = schema?.properties || {};

    return (
        <>
            {Object.entries(properties).map(([key, prop]) => {
                const type = Array.isArray(prop.type) ? prop.type[0] : prop.type;
                const label = prop.title || key;
                const helperText = prop.description || '';
                const handle = (v) => onChange({ ...value, [key]: v });

                if (prop.enum) {
                    const choices = prop.enum.map((v) => ({ id: v, name: String(v) }));
                    return (
                        <SelectInput
                            key={key}
                            source={key}
                            label={label}
                            choices={choices}
                            helperText={helperText}
                            fullWidth
                            onChange={(e) => handle(e.target.value)}
                        />
                    );
                }

                switch (type) {
                    case 'boolean':
                        return (
                            <BooleanInput
                                key={key}
                                source={key}
                                label={label}
                                helperText={helperText}
                                onChange={(e) => handle(!!e.target.checked)}
                            />
                        );
                    case 'integer':
                    case 'number':
                        return (
                            <NumberInput
                                key={key}
                                source={key}
                                label={label}
                                helperText={helperText}
                                onChange={(e) => handle(Number(e.target.value))}
                            />
                        );
                    case 'object':
                        return (
                            <TextInput
                                key={key}
                                source={key}
                                label={`${label} (JSON)`}
                                helperText={helperText}
                                multiline
                                fullWidth
                                onChange={(e) => {
                                    try {
                                        const parsed = e.target.value ? JSON.parse(e.target.value) : {};
                                        handle(parsed);
                                    } catch {
                                        handle(e.target.value);
                                    }
                                }}
                            />
                        );
                    default: // string and others
                        return (
                            <TextInput
                                key={key}
                                source={key}
                                label={label}
                                helperText={helperText}
                                fullWidth
                                onChange={(e) => handle(e.target.value)}
                            />
                        );
                }
            })}
        </>
    );
};

const usePromptTemplate = (id) => {
    const { data } = useGetOne('prompt_templates', { id }, { enabled: !!id });
    return data;
};

// Create view: bespoke dynamic form to guide generation via template
export const CourseWizardCreate = () => {
    const notify = useNotify();
    const redirect = useRedirect();

    const [templateId, setTemplateId] = useState(null);
    const template = usePromptTemplate(templateId);
    const schema = template?.params_schema || null;

    const [params, setParams] = useState({});
    const [ragKbSource, setRagKbSource] = useState('manual_kb');
    const [saveAs, setSaveAs] = useState('draft');
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        // reset params when template changes
        setParams({});
    }, [templateId]);

    const onSubmit = async () => {
        if (isGenerating) return; // Prevent multiple submissions

        try {
            setIsGenerating(true);
            notify('Please wait 1-3 minutes while we generate your course...', { type: 'info' });

            const backendBase = process.env.REACT_APP_BACKEND_BASE ?? 'http://localhost:8000';
            const url = `${backendBase}/api/courses/generate_from_template`;
            const userId = localStorage.getItem('userId');
            const token = localStorage.getItem('token');

            // Ensure templateId is properly formatted as a UUID string
            if (!templateId) {
                notify('Please select a template first', { type: 'warning' });
                setIsGenerating(false);
                return;
            }

            const payload = {
                prompt_template_id: templateId,
                params: params || {}, // Ensure params is always an object
                course_meta: { rag_kb_source: ragKbSource },
                save_as: saveAs,
            };

            console.log('Submitting payload:', JSON.stringify(payload, null, 2));
            console.log('Template ID type:', typeof templateId);
            console.log('Template ID value:', templateId);
            console.log('Params:', params);

            const headers = {
                'Content-Type': 'application/json',
                'X-User-Id': userId || '',
            };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            console.log('Headers:', headers);

            const response = await axios.post(url, payload, { headers });
            console.log('Success response:', response.data);
            notify('Course generation completed successfully!', { type: 'success' });
            redirect('/course_wizard');
        } catch (e) {
            console.error('Generation error:', e);
            if (e.response && e.response.data) {
                console.error('Error response data:', e.response.data);
                notify(`Failed: ${e.response.data.detail || e.message}`, { type: 'error' });
            } else {
                notify('Failed to start generation', { type: 'warning' });
            }
        } finally {
            setIsGenerating(false);
        }
    };

    // Custom form toolbar with generate button
    const GenerateToolbar = () => (
        <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '8px 0'
        }}>
            <button
                onClick={onSubmit}
                disabled={isGenerating}
                className="MuiButton-root MuiButton-contained MuiButton-containedPrimary"
                style={{
                    margin: '8px',
                    opacity: isGenerating ? 0.6 : 1,
                    cursor: isGenerating ? 'not-allowed' : 'pointer'
                }}
            >
                {isGenerating ? 'Generating... Please wait 1-3 minutes' : 'Generate'}
            </button>
        </div>
    );

    return (
        <Create title="Generate Course Wizard">
            <SimpleForm toolbar={<GenerateToolbar />} onSubmit={() => false}>
                <div className="MuiTypography-root MuiTypography-h6" style={{ marginBottom: '16px' }}>
                    Generate Course Material
                </div>
                <div className="MuiFormHelperText-root" style={{ marginBottom: '16px' }}>
                    Select a template to see its parameters
                </div>
                <ReferenceInput
                    source="prompt_template_id"
                    reference="prompt_templates"
                    label="Prompt Template"
                    perPage={50}
                    fullWidth
                >
                    <SelectInput optionText="name" onChange={(e) => setTemplateId(e.target.value)} />
                </ReferenceInput>

                {schema ? (
                    <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                        <div className="MuiTypography-root MuiTypography-subtitle1" style={{ marginBottom: '8px' }}>
                            Parameters
                        </div>
                        <SchemaFields schema={schema} value={params} onChange={setParams} />
                    </div>
                ) : null}

                <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <TextInput
                        source="rag_kb_source"
                        label="RAG KB Source"
                        value={ragKbSource}
                        onChange={(e) => setRagKbSource(e.target.value)}
                        helperText="Path to knowledge base file (e.g., RAG_KB/consolidated_manual_knowledge_base.md)"
                        fullWidth
                    />
                    <div
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '32px',
                            cursor: 'pointer',
                            zIndex: 1000
                        }}
                        onClick={(e) => {
                            // Prevent event propagation
                            e.preventDefault();
                            e.stopPropagation();
                            // Trigger hidden file input click
                            document.getElementById('file-input').click();
                        }}
                        title="Browse knowledge bases"
                    >
                        <FolderIcon style={{ color: '#757575' }} />
                    </div>
                    <input
                        type="file"
                        id="file-input"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                                const file = e.target.files[0];
                                // Use the full path if available (Electron/desktop), otherwise use webkitRelativePath or path
                                const fullPath = file.path || file.webkitRelativePath || file.name;
                                setRagKbSource(fullPath);
                            }
                        }}
                    />
                </div>

                <SelectInput
                    source="save_as"
                    label="Save As"
                    choices={[
                        { id: 'draft', name: 'Draft' },
                        { id: 'final', name: 'Final' },
                    ]}
                    value={saveAs}
                    onChange={(e) => setSaveAs(e.target.value)}
                    fullWidth
                />
            </SimpleForm>
        </Create>
    );
};

// Basic edit (read-only-ish) showing status, linking to generated content fields
export const CourseWizardEdit = () => (
    <Edit title="Course Wizard">
        <SimpleForm>
            <TextInput source="status" />
            <NumberInput source="version" />
            <TextInput source="rag_kb_source" />
            <TextInput source="prompt_template_snapshot" multiline fullWidth />
            <TextInput source="params" multiline fullWidth />
            <TextInput source="material" multiline fullWidth />
        </SimpleForm>
    </Edit>
);
